import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
//import './CustomerReturnsList.css';
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormGroup,
    Input,
    Label,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Spinner
} from 'reactstrap';
import {
    FaAngleDoubleLeft,
    FaAngleDoubleRight,
    FaAngleLeft,
    FaAngleRight,
    FaCheckCircle,
    FaEdit,
    FaFilePdf,
    FaFilter,
    FaMoneyBillWave,
    FaPlus,
    FaSearch,
    FaTrash,
    FaTimesCircle,
    FaUndo
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { customerReturnActions } from '../../sagas/customerReturnSlice';
import { formatPrice } from '../../helpers/utility';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Select from "react-select";
import { clientActions } from '../../sagas/clientSlice';
import { companyActions } from '../../sagas/companySlice';
import './CustomerReturns.css';

const CustomerReturnsList = () => {
    const dispatch = useDispatch();
    const { customerReturns, loading } = useSelector((state) => state.customerReturns);
    const { t } = useTranslation();
    const { company } = useSelector((s) => s.auth);
    const { companies } = useSelector((state) => state.companies);
    const defaultCompanyId = useMemo(
        () => companies?.find((item) => item.isDefault)?.id ?? companies?.[0]?.id ?? null,
        [companies]
    );

    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const searchInputRef = useRef(null);
    const clients = useSelector(state => state.clients.clients);

    // Filter states
    const [filters, setFilters] = useState({
        companyId: null,
        clientId: '',
        dateFrom: formatDate(todayDate),
        dateTo: formatDate(tomorrowDate),
        query: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const navigate = useNavigate();

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [customerReturns, filters]);

    useEffect(() => {
        if (clients && clients.length == 0) {
            dispatch(clientActions.search({ query: "" }));
        }
        dispatch(companyActions.getAll({}));
    }, []);

    useEffect(() => {
        if (defaultCompanyId && filters.companyId == null) {
            setFilters((prev) => ({ ...prev, companyId: defaultCompanyId }));
        }
    }, [defaultCompanyId]);

    useEffect(() => {
        dispatch(customerReturnActions.list({
            ...filters,
        }));
    }, [filters.companyId, filters.clientId, filters.dateFrom, filters.dateTo]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filters.companyId, filters.clientId, filters.dateFrom, filters.dateTo, filters.query, rowsPerPage]);


    const debouncedSearch = () => {
        dispatch(customerReturnActions.list({
            ...filters,
        }));
    }

    const clearFilters = () => {
        setFilters({
            query: '',
            companyId: defaultCompanyId,
            clientId: null,
            dateFrom: formatDate(todayDate),
            dateTo: formatDate(tomorrowDate)
        });
        debouncedSearch();
    };

    const clientOptions = clients?.map(client => ({
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom || ''} `
            : client.entreprise,
        chantiers: client.chantiers || []
    })) ?? [];

    const clientOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...clientOptions
    ];

    const companyOptions = companies?.map(item => ({
        value: item.id,
        label: item.name,
    })) ?? [];

    const companyOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...companyOptions
    ];

    const compactSelectStyles = {
        control: (base) => ({ ...base, minHeight: '31px', fontSize: '0.875rem' }),
        valueContainer: (base) => ({ ...base, padding: '0 8px' }),
        input: (base) => ({ ...base, margin: 0, padding: 0 }),
        indicatorsContainer: (base) => ({ ...base, height: '31px' }),
        clearIndicator: (base) => ({ ...base, padding: '4px' }),
        dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };

    const totalPages = Math.max(1, Math.ceil((customerReturns?.length || 0) / rowsPerPage));
    const currentPageSafe = Math.min(currentPage, totalPages);
    const paginatedReturns = useMemo(() => {
        const rows = customerReturns || [];
        const start = (currentPageSafe - 1) * rowsPerPage;
        return rows.slice(start, start + rowsPerPage);
    }, [customerReturns, currentPageSafe, rowsPerPage]);

    const pageStart = !customerReturns?.length ? 0 : (currentPageSafe - 1) * rowsPerPage + 1;
    const pageEnd = Math.min(currentPageSafe * rowsPerPage, customerReturns?.length || 0);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const getStatusBadge = (statut) => {
        if (statut === 'BROUILLON') return <span className="badge bg-warning">{t('customerReturns.brouillon')}</span>;
        if (statut === 'ANNULE') return <span className="badge bg-danger">{t('customerReturns.annule')}</span>;
        if (statut === 'VALIDE') return <span className="badge bg-success">{t('customerReturns.valide')}</span>;
        if (statut === 'COMPTABILISE') return <span className="badge bg-primary">{t('customerReturns.comptabilise', 'Comptabilise')}</span>;
        return <span className="badge bg-secondary">{statut || '-'}</span>;
    };

    const getModeBadge = (mode) => {
        if (mode === 'AVOIR') return <span className="badge bg-primary">{t('customerReturns.avoir')}</span>;
        if (mode === 'REMBOURSEMENT') return <span className="badge bg-info">{t('customerReturns.remboursement')}</span>;
        if (mode === 'ECHANGE') return <span className="badge bg-dark">{t('customerReturns.echange')}</span>;
        return <span className="badge bg-secondary">{mode || '-'}</span>;
    };


    const handleCreateReturn = () => {
        navigate("/sales-module/returns/new");
    };

    const handleEdit = (returnItem) => {
        navigate("/sales-module/returns/" + returnItem.id);
    }

    const fmt = (n) => {
        const x = Number(n || 0);
        const s = (Math.round((x + Number.EPSILON) * 100) / 100).toFixed(2);
        return s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };
    const safe = (v, fb = '-') => (v || v === 0 ? v : fb);
    const toDateFR = (d) => {
        try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return '-'; }
    };

    const generateReturnReceipt = (returnItem, t = (k) => k, opts = {}) => {
        const {
            devise = 'DH',
            open = true,
            fileName = `BON_RETOUR_${safe(returnItem?.numeroRetour, returnItem?.id)}.pdf`,
        } = opts;



        // Récup lignes robustes
        const rawLines = returnItem?.lignes || returnItem?.lignesRetour || returnItem?.lines || [];
        // Mapping standard: code, libellé, qte, prixHT, tva, totalTTC
        const rows = rawLines.map((ln) => {
            const code = ln.articleCode || '';
            const name = ln.articleNom || '';
            const unite = ln.unite || '';
            const qte = Number(ln.quantiteRetournee || 0);
            const tva = Number(ln.tauxTVA ?? 0);
            const puTTC = Number(ln.prixUnitaireTTC || 0);
            const totalTTC = Number(ln.montantTTC || (qte * puTTC));

            return { code, name, qte, unite, tva, puTTC, totalTTC };
        });


        // Totaux (fallback sur champs déjà agrégés si présents)
        const totalTTC = Number(returnItem?.montantTTC, 0);

        // En-têtes & métadonnées
        const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        doc.setProperties({
            title: `Bon de Retour - ${safe(returnItem?.numeroRetour, returnItem?.id)}`,
            author: safe(company?.name, 'Entreprise'),
            subject: 'Bon de retour client',
        });

        const primary = [6, 145, 43]; // #06912b
        const ink = [15, 23, 42];     // #0F172A
        const subtle = [100, 116, 139];

        // HEADER ENTREPRISE
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(...ink);
        doc.text(safe(company?.name, 'Entreprise'), 40, 50);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(...subtle);
        const addrLines = [
            safe(company?.address, ''),
            [company?.phone1, company?.phone2].filter(Boolean).length ? `Tél: ${[company.phone1, company.phone2].filter(Boolean).join(' / ')}` : '',
            safe(company?.email, ''),
        ].filter(Boolean);
        let y = 70;
        addrLines.forEach(line => { doc.text(String(line), 40, y); y += 14; });

        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(...primary);
        doc.text('BON DE RETOUR', pageWidth - 20, 40, { align: 'right' });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(...subtle);

        doc.text(`# ${safe(returnItem?.numeroRetour, returnItem?.id)}`, pageWidth - 20, 58, { align: 'right' });
        doc.text(toDateFR(returnItem?.dateOperation || new Date()), pageWidth - 20, 72, { align: 'right' });

        // BLOC CLIENT
        const clientName = safe(returnItem?.clientNom, '-');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...ink);
        doc.text('Client', 40, 120);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(...ink);
        doc.text(safe(clientName, '-'), 40, 138);

        // MODE (AVOIR, REMBOURSEMENT, ECHANGE) + STATUT
        const modeMap = {
            'AVOIR': 'Avoir',
            'REMBOURSEMENT': 'Remboursement',
            'ECHANGE': 'Échange',
        };
        const statutMap = {
            'BROUILLON': 'Brouillon',
            'VALIDE': 'Validé',
            'ANNULE': 'Annulé',
            'COMPTABILISE': 'Comptabilisé',
        };

        const modeTxt = modeMap[returnItem?.modeRemboursement] || safe(returnItem?.modeRemboursement, '-');
        const statutTxt = statutMap[returnItem?.statut] || safe(returnItem?.statut, '-');

        doc.setTextColor(...subtle);
        doc.text(`Mode: ${modeTxt}`, 40, 160);
        doc.text(`Statut': ${statutTxt}`, 40, 174);

        // TABLE LIGNES
        const columns = [
            { header: 'Code', dataKey: 'code' },
            { header: 'Désignation', dataKey: 'name' },
            { header: 'Qté', dataKey: 'qte' },
            { header: 'Unité', dataKey: 'unite' },
            { header: 'PU TTC', dataKey: 'puTTC' },
            { header: 'TVA %', dataKey: 'tva' },
            { header: 'Total TTC', dataKey: 'totalTTC' },
        ];


        const data = rows.map(r => ({
            code: r.code,
            name: r.name,
            qte: r.qte,
            unite: r.unite,
            puTTC: `${fmt(r.puTTC)} ${devise}`,
            tva: `${Math.round(r.tva * 100)}%`,
            totalTTC: `${fmt(r.totalTTC)} ${devise}`,
        }));

        autoTable(doc, {
            startY: 200,
            head: [columns.map(c => c.header)],
            body: data.map(row => columns.map(c => row[c.dataKey])),
            styles: {
                fontSize: 9,
                cellPadding: 6,
                valign: 'middle',
            },
            headStyles: {
                fillColor: primary,
                textColor: 255,
                fontStyle: 'bold',
            },
            alternateRowStyles: { fillColor: [245, 247, 250] },
            theme: 'striped',
            columnStyles: {
                2: { halign: 'left', cellWidth: 45 },   // Qté
                3: { halign: 'left', cellWidth: 80 },   // PU TTC
                4: { halign: 'left', cellWidth: 55 },  // TVA
                5: { halign: 'left', cellWidth: 90 },   // Total
            },
        });

        // RÉCAPITULATIF (à droite)
        const endY = doc.lastAutoTable.finalY || 200;
        const recapX = 300;
        let ry = Math.max(endY + 24, 500); // sécurité visuelle

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...ink);
        doc.text('Récapitulatif', recapX, ry);
        ry += 16;

        doc.setFont('helvetica', 'bold');
        doc.text(`Total TTC:`, recapX, ry);
        doc.text(`${fmt(totalTTC)} ${devise}`, 540, ry, { align: 'right' });
        ry += 20;

        // NOTE POLIE
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(9);
        doc.setTextColor(...subtle);
        const polite =
            'Merci pour votre confiance. Ce reçu atteste le retour des articles indiqués.';
        doc.text(polite, 40, ry);

        // PIED DE PAGE
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...subtle);
        doc.text(safe(company?.name, 'Entreprise'), 40, 820);
        doc.text(`'Document généré le ${toDateFR(new Date())}`, 540, 820, { align: 'right' });

        if (open) {
            doc.output('dataurlnewwindow'); // ouvre dans un nouvel onglet
        } else {
            doc.save(fileName);
        }
    };

    const handleValidateReturn = (returnItem) => {
        Swal.fire({
            title: t('customerReturns.confirmValidate'),
            text: t('customerReturns.confirmValidateMessage'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            let payload = {
                id: returnItem.id,
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.updated'),
                        text: t('customerReturns.validatedSuccess'),
                    });
                    // Refresh current page
                    const refreshPayload = {
                        ...filters
                    };
                    dispatch(customerReturnActions.list(refreshPayload));
                },
            };
            dispatch(customerReturnActions.validate(payload));
        });
    }

    const handleDeleteReturn = (returnItem) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('customerReturns.confirmDelete'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            let payload = {
                id: returnItem.id,
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.deleted'),
                        text: t('customerReturn.deletedSuccess'),
                    });
                    // Refresh current page
                    const refreshPayload = {
                        ...filters
                    };
                    dispatch(customerReturnActions.list(refreshPayload));
                },
            };
            dispatch(customerReturnActions.delete(payload));
        });
    }

    const handleCancelReturn = (returnItem) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('customerReturns.messages.confirmCancel', 'Voulez-vous annuler ce retour client ?'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
            confirmButtonColor: '#dc3545',
        }).then((result) => {
            if (!result.isConfirmed) return;
            dispatch(customerReturnActions.cancel({
                id: returnItem.id,
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.updated'),
                        text: t('customerReturns.messages.cancelSuccess', 'Retour client annulé avec succès'),
                    });
                    dispatch(customerReturnActions.list({ ...filters }));
                },
                onError: () => {
                    Swal.fire({
                        icon: 'error',
                        title: t('common.error'),
                        text: t('customerReturns.messages.cancelError', "Impossible d'annuler ce retour client"),
                    });
                },
            }));
        });
    }

    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaUndo className="text-primary me-2" />
                                {t('customerReturns.list')}
                            </h4>
                            <div className="d-flex align-items-center">
                                <Button color="primary" onClick={handleCreateReturn}>
                                    <FaPlus className="me-2" /> {t('customerReturns.new')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="filter-container mb-4">
                                <Row className="mb-3 g-2 align-items-end">
                                    <Col lg={3} md={4} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label className="mb-1 small">{t('reglement.search')}</Label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <FaSearch />
                                                </span>
                                                <Input
                                                    type="text"
                                                    innerRef={searchInputRef}
                                                    placeholder={t('customerReturns.searchPlaceholder')}
                                                    value={filters.query}
                                                    onChange={e => {
                                                        const value = e.target.value.toUpperCase();
                                                        setFilters(prevFilters => ({
                                                            ...prevFilters,
                                                            query: value
                                                        }));
                                                    }}
                                                    className="text-uppercase"
                                                    style={{ textTransform: 'uppercase' }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            debouncedSearch();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </FormGroup>
                                    </Col>
                                    <Col lg={3} md={4} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label htmlFor="companyId" className="mb-1 small">{t('company.company', 'Entreprise')}:</Label>
                                            <Select
                                                id="companyId"
                                                options={companyOptionsWithAll}
                                                value={companyOptionsWithAll.find(option => option.value === filters.companyId)}
                                                onChange={(selectedOption) => {
                                                    setFilters(f => ({ ...f, companyId: selectedOption ? selectedOption.value : null }));
                                                }}
                                                placeholder={t('company.company', 'Entreprise')}
                                                isSearchable
                                                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                styles={compactSelectStyles}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={3} md={4} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label htmlFor="clientId" className="mb-1 small">{t('bonLivraison.client')}:</Label>
                                            <Select
                                                id="clientId"
                                                options={clientOptionsWithAll}
                                                value={clientOptionsWithAll.find(option => option.value === filters.clientId)}
                                                onChange={(selectedOption) => {
                                                    setFilters(f => ({ ...f, clientId: selectedOption ? selectedOption.value : null }));
                                                }}
                                                placeholder={t('bonLivraison.selectClient')}
                                                isSearchable
                                                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                styles={compactSelectStyles}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateFrom" className="mb-1">Date début</Label>
                                            <Input
                                                type="date"
                                                id="dateFrom"
                                                value={filters.dateFrom}
                                                onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateTo" className="mb-1">Date fin</Label>
                                            <Input
                                                type="date"
                                                id="dateTo"
                                                value={filters.dateTo}
                                                onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                                        <Button
                                            color="secondary"
                                            onClick={clearFilters}
                                            title={t('common.clearFilters')}
                                            className="mb-3"
                                            style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <FaFilter />
                                        </Button>
                                    </Col>
                                    <Col lg={1} md={2} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label className="mb-1 small">{t('common.lines', 'Lignes')}</Label>
                                            <Input
                                                type="select"
                                                value={rowsPerPage}
                                                bsSize="sm"
                                                onChange={e => setRowsPerPage(Number(e.target.value))}
                                            >
                                                {[10, 20, 50, 100].map((size) => (
                                                    <option key={size} value={size}>{size}</option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>
                            <div className="enterprise-table-scroll">
                                <table className="table table-hover table-striped table-bordered document-grouped-table return-grouped-table" style={{ fontSize: '0.8rem' }}>
                                    <thead className="thead-light">
                                        <tr>
                                            <th className="align-middle text-center">{t('customerReturns.numero')}</th>
                                            <th className="align-middle text-center">{t('customerReturns.client')}</th>
                                            <th className="align-middle text-center">{t('facture.table.amounts', 'Montants')}</th>
                                            <th className="align-middle text-center">{t('customerReturns.status')}</th>
                                            <th className="align-middle text-center">{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedReturns.map((returnItem) => (
                                            <tr key={returnItem.id}>
                                                <td className="align-middle py-1">
                                                    <div className="document-main-ref">{returnItem.numeroRetour}</div>
                                                    <div className="document-subtext">{t('customerReturns.date')}: {toDateFR(returnItem.dateOperation)}</div>
                                                    <div className="document-subtext">{t('common.user')}: {returnItem.createdBy || 'N/A'}</div>
                                                    <div className="document-subtext">{t('company.company', 'Entreprise')}: {returnItem.companyName || '-'}</div>
                                                </td>
                                                <td className="align-middle py-1">
                                                    <div className="document-strong">{returnItem.clientNom || 'N/A'}</div>
                                                    <div className="document-subtext">{t('delivery.commercial')}: {returnItem.commercialNom || 'N/A'}</div>
                                                    <div className="document-subtext">{t('bonLivraison.transporteur')}: {returnItem.transporteurName || '-'}</div>
                                                    {returnItem.motifRetour && (
                                                        <div className="document-subtext">{t('customerReturns.form.fields.reason')}: {returnItem.motifRetour}</div>
                                                    )}
                                                </td>
                                                <td className="align-middle">
                                                    <div className="document-amount-grid">
                                                        <span>TTC</span><strong>{formatPrice(returnItem.montantTTC, false)}</strong>
                                                        <span>HT</span><span>{formatPrice(returnItem.montantHT, false)}</span>
                                                        <span>TVA</span><span>{formatPrice(returnItem.montantTVA, false)}</span>
                                                    </div>
                                                </td>
                                                <td className="align-middle text-center">
                                                    {getStatusBadge(returnItem.statut)}
                                                    <div className="mt-1">{getModeBadge(returnItem.modeRemboursement)}</div>
                                                </td>
                                                <td className="align-middle text-center">
                                                    <div className="btn-group">
                                                        <Button
                                                            color="secondary"
                                                            title={t('customerReturns.generatePdf')}
                                                            onClick={() => generateReturnReceipt(returnItem)}
                                                            className="me-1"
                                                            size="sm"
                                                        >
                                                            <FaFilePdf />
                                                        </Button>
                                                        {returnItem.statut === 'BROUILLON' && (
                                                            <Button
                                                                color="success"
                                                                title={t('customerReturns.validate')}
                                                                onClick={() => handleValidateReturn(returnItem)}
                                                                className="me-1"
                                                                size="sm"
                                                            >
                                                                <FaCheckCircle />
                                                            </Button>
                                                        )}

                                                        <Button
                                                            color="warning"
                                                            title={t('customerReturns.edit')}
                                                            onClick={() => handleEdit(returnItem)}
                                                            size="sm"
                                                            className="me-1"
                                                        >
                                                            <FaEdit />
                                                        </Button>

                                                        
                                                        {returnItem.statut !== 'ANNULE' && !returnItem.estRembourse && (
                                                            <Button
                                                                color="danger"
                                                                outline
                                                                title={t('customerReturns.messages.confirmCancel', 'Annuler le retour')}
                                                                onClick={() => handleCancelReturn(returnItem)}
                                                                className="me-1"
                                                                size="sm"
                                                            >
                                                                <FaTimesCircle />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {
                                            customerReturns.length === 0 && (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-4">
                                                        <div className="text-muted">
                                                            {
                                                                t('customerReturns.noReturns')
                                                            }
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-light fw-bold">
                                            <td colSpan="2" className="text-end">{t('common.totals')}</td>
                                            <td className="align-middle">
                                                <div className="document-amount-grid">
                                                    <span>TTC</span><strong>{formatPrice(customerReturns.reduce((sum, ret) => sum + (ret.montantTTC || 0), 0), false)}</strong>
                                                    <span>HT</span><span>{formatPrice(customerReturns.reduce((sum, ret) => sum + (ret.montantHT || 0), 0), false)}</span>
                                                    <span>TVA</span><span>{formatPrice(customerReturns.reduce((sum, ret) => sum + (ret.montantTVA || 0), 0), false)}</span>
                                                </div>
                                            </td>
                                            <td colSpan="2"></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3">
                                <div className="text-muted small">
                                    {t('common.paginationInfo', {
                                        defaultValue: '{{start}}-{{end}} sur {{total}}',
                                        start: pageStart,
                                        end: pageEnd,
                                        total: customerReturns.length,
                                    })}
                                </div>
                                <Pagination size="sm" className="mb-0">
                                    <PaginationItem disabled={currentPageSafe === 1}>
                                        <PaginationLink first onClick={() => handlePageChange(1)}>
                                            <FaAngleDoubleLeft />
                                        </PaginationLink>
                                    </PaginationItem>
                                    <PaginationItem disabled={currentPageSafe === 1}>
                                        <PaginationLink previous onClick={() => handlePageChange(currentPageSafe - 1)}>
                                            <FaAngleLeft />
                                        </PaginationLink>
                                    </PaginationItem>
                                    <PaginationItem disabled>
                                        <PaginationLink>{currentPageSafe} / {totalPages}</PaginationLink>
                                    </PaginationItem>
                                    <PaginationItem disabled={currentPageSafe === totalPages}>
                                        <PaginationLink next onClick={() => handlePageChange(currentPageSafe + 1)}>
                                            <FaAngleRight />
                                        </PaginationLink>
                                    </PaginationItem>
                                    <PaginationItem disabled={currentPageSafe === totalPages}>
                                        <PaginationLink last onClick={() => handlePageChange(totalPages)}>
                                            <FaAngleDoubleRight />
                                        </PaginationLink>
                                    </PaginationItem>
                                </Pagination>
                            </div>

                        </>
                    )}
                </CardBody>
            </Card>

        </Container>
    );
};

export default CustomerReturnsList;

